import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Button } from '@/components/atoms';
import { formatarDataHora } from '@/i18n/formato';
import { ConnectorLoadPanel } from '@/components/FeedDrive/ConnectorLoadPanel';
import {
  CostReadout,
  RunStateCard,
  type TenantRunState,
} from '@/components/FeedDrive/TenantOperationPrimitives';
import { ApiError } from '@/services/api';
import { detalheLegivel } from '@/services/erros';
import { usePlatformAccess } from '@/services/platform';
import {
  feedDriveService,
  type PoolEvent,
  type PoolEventsResponse,
  type PoolHealth,
  type PoolPriority,
  type TenantAutomationInput,
  type TenantCuration,
  type TenantCurationInput,
  type TenantSubscription,
  type TenantSubscriptionInput,
  type TenantAutomation,
  type TenantTrigger,
  type TenantTriggerInput,
  type TenantWatchlist,
  type TenantWatchlistInput,
  type AutomationRun,
  type WireFact,
} from '@/services/feeddrive';
import { useFeedDriveEstado } from '@/services/feeddriveEstado';
import { useAuth } from '@/store/useAuth';

import styles from './FeedDrive.module.css';

type Tab = 'terminal' | 'connectors' | 'subscriptions' | 'automations';
type DetailTarget = { kind: 'fact' | 'wire'; id: string } | null;

const TABS: Array<{ id: Tab; label: string; description: string }> = [
  { id: 'terminal', label: 'Terminal', description: 'Acontecimentos e mudanças do NEWS POOL' },
  { id: 'connectors', label: 'Conectores', description: 'Carga e configuração operacional' },
  { id: 'subscriptions', label: 'Assinaturas', description: 'O que esta redação acompanha' },
  {
    id: 'automations',
    label: 'Automações',
    description: 'Configurações para testes supervisionados',
  },
];

/**
 * O espaço pool-tenant tem dono: assinatura, automação e curadoria carregam
 * entitlement e auditoria do tenant (B-36). O backend recusa a mutação de staff
 * com 400 "a mutação exige um tenant"; sem esta parede o staff preenchia o
 * formulário inteiro para descobrir isso no POST. Um literal só, porque banner e
 * `title` do botão precisam dizer exatamente o mesmo motivo.
 */
const PAREDE_STAFF_B36 =
  'Assinaturas e automações pertencem ao tenant (decisão B-36) — a casa observa, não opera. Entre com a conta do cliente para criar.';

/**
 * O inverso da parede acima. A carga das fontes do NEWS POOL é operação de
 * plataforma: o GET /feeddrive/connectors só responde para staff (B-36). Para
 * tenant/enterprise a aba pintava "0 cadastrados" em cima de uma chamada que
 * falhou — o cliente lia "o pool não tem fonte nenhuma" (F-494).
 */
const PAREDE_TENANT_B36 =
  'O console de conectores é operado pela casa (decisão B-36) — a carga das fontes do NEWS POOL não é configurável pelo tenant.';

const EVENT_OPTIONS = [
  'wire_fact.created',
  'wire_fact.revised',
  'wire_fact.retracted',
  'wire_fact.conflicted',
  'coverage.created',
  'enrichment.completed',
  'wire.created',
  'wire.updated',
  'wire.merged',
  'source.degraded',
  'source.recovered',
  'pool.gap_detected',
];

const EVENT_LABELS: Record<string, string> = {
  'wire_fact.created': 'Fato novo',
  'wire_fact.revised': 'Fato corrigido',
  'wire_fact.retracted': 'Fato retraído',
  'wire_fact.conflicted': 'Fato contradito',
  'coverage.created': 'Nova cobertura',
  'enrichment.completed': 'Análise concluída',
  'wire.created': 'Fio criado',
  'wire.updated': 'Fio atualizado',
  'wire.merged': 'Fios unidos',
  'source.degraded': 'Fonte degradada',
  'source.recovered': 'Fonte recuperada',
  'pool.gap_detected': 'Intervalo possivelmente perdido',
};

const EPISTEMIC_LABELS: Record<string, string> = {
  measurement: 'Medição',
  official_record: 'Registro oficial',
  publication_event: 'Publicação',
  attributed_statement: 'Declaração atribuída',
  transaction: 'Transação',
  calculated_change: 'Variação calculada',
  derived_assertion: 'Análise derivada',
};

const LIFECYCLE_LABELS: Record<string, string> = {
  observed: 'Observado',
  preliminary: 'Preliminar',
  active: 'Ativo',
  revised: 'Corrigido',
  retracted: 'Retraído',
  cancelled: 'Cancelado',
  open: 'Aberto',
  watching: 'Em acompanhamento',
  closed: 'Fechado',
  reopened: 'Reaberto',
  merged: 'Unido a outro fio',
  split: 'Desmembrado',
  tombstoned: 'Removido por retenção',
};

function apiError(error: unknown): string {
  if (error instanceof ApiError) {
    const detail = (error.body as { detail?: unknown } | null)?.detail;
    if (typeof detail === 'string') return detail;
    if (detail) return JSON.stringify(detail);
    return `Falha HTTP ${error.status}`;
  }
  return error instanceof Error ? error.message : 'Falha desconhecida.';
}

function ControlError({ error, action }: { error: unknown; action: string }) {
  if (!error) return null;
  return (
    <p role="alert" className={styles.controlError}>
      {apiError(error)} {action}
    </p>
  );
}

function parseList(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatFilterValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string').join(', ');
  }
  return '';
}

function setFilterValue(
  filters: Record<string, unknown>,
  key: string,
  value: string,
  preserveArray: boolean,
): Record<string, unknown> {
  const values = parseList(value);
  const withoutKey = Object.fromEntries(
    Object.entries(filters).filter(([currentKey]) => currentKey !== key),
  );
  if (values.length === 0) return withoutKey;
  return {
    ...withoutKey,
    [key]: preserveArray || values.length > 1 ? values : values[0],
  };
}

function dateTime(value?: string | null): string {
  if (!value) return 'não informado';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return formatarDataHora(parsed);
}

function relativeTime(value?: string | null): string {
  if (!value) return 'sem registro';
  const seconds = Math.max(0, (Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return `${Math.floor(seconds)} s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h`;
  return `${Math.floor(seconds / 86400)} d`;
}

function seconds(value?: number | null): string {
  if (value == null) return 'não medido';
  if (value < 60) return `${value}s`;
  if (value < 3600) return `${Math.round(value / 60)}min`;
  return `${(value / 3600).toFixed(1)}h`;
}

function pct(value?: number | null): string {
  return value == null ? 'não medido' : `${Math.round(value * 100)}%`;
}

function priorityTone(priority: PoolPriority): 'neutral' | 'info' | 'warn' | 'crit' {
  if (priority === 'critical' || priority === 'urgent') return 'crit';
  if (priority === 'high') return 'warn';
  if (priority === 'normal') return 'info';
  return 'neutral';
}

function stateTone(state: string): 'neutral' | 'info' | 'ok' | 'warn' | 'crit' {
  if (['active', 'healthy', 'completed', 'executed', 'open', 'recovered'].includes(state))
    return 'ok';
  if (['shadow', 'supervised', 'watching', 'reopened'].includes(state)) return 'info';
  if (['degraded', 'held', 'paused', 'revised', 'split', 'preliminary'].includes(state))
    return 'warn';
  if (
    [
      'failed',
      'error',
      'unavailable',
      'retracted',
      'cancelled',
      'conflicted',
      'gap',
      'tombstoned',
    ].includes(state)
  )
    return 'crit';
  return 'neutral';
}

function eventTone(eventName: string): 'neutral' | 'info' | 'ok' | 'warn' | 'crit' {
  if (
    eventName.includes('retracted') ||
    eventName.includes('conflicted') ||
    eventName.includes('gap')
  )
    return 'crit';
  if (eventName.includes('revised') || eventName.includes('degraded')) return 'warn';
  if (eventName.includes('recovered')) return 'ok';
  return 'info';
}

function eventTitle(event: PoolEvent): string {
  return EVENT_LABELS[event.event_name] ?? event.event_name;
}

function factValue(fact: WireFact): string {
  const value = fact.fact.object.value;
  const rendered = value == null ? 'valor não informado' : String(value);
  return [rendered, fact.fact.object.unit, fact.fact.object.currency].filter(Boolean).join(' ');
}

export function FeedDrive() {
  const { t } = useTranslation([
    'common',
    'nav',
    'board',
    'articles',
    'analytics',
    'feeddrive',
    'config',
    'strategist',
  ]);
  const [tab, setTab] = useState<Tab>('terminal');
  const [detail, setDetail] = useState<DetailTarget>(null);
  // Sonda no topo da página, não dentro das abas: as abas montam e desmontam
  // conforme `tab`, e um hook atrás dessa condição quebraria a ordem do React.
  const { isStaff } = usePlatformAccess();
  // Pergunta ANTES de montar as abas: cada aba consulta o FeedDrive ao montar, e
  // numa instalacao sem ele (o GNC DESK, 11/09/2026) isso eram 9 alarmes
  // vermelhos contra um servico que ali nunca existiu.
  const estado = useFeedDriveEstado();

  if (estado.data?.habilitado !== true) {
    const aviso = estado.isPending
      ? 'Verificando se o FeedDrive está ligado nesta instalação.'
      : estado.isError
        ? 'Não foi possível saber se o FeedDrive está ligado nesta instalação.'
        : 'O FeedDrive não está ligado nesta instalação. Não há nada para consultar aqui.';
    return (
      <section className={styles.root} aria-label={t('feeddrive:title', 'FeedDrive')}>
        <header className={styles.header}>
          <div>
            <span className={styles.eyebrow}>Inteligência de fontes</span>
            <h1>NEWS TERMINAL</h1>
            <p role="status">{aviso}</p>
          </div>
        </header>
      </section>
    );
  }

  return (
    <section className={styles.root} aria-label={t('feeddrive:title', 'FeedDrive')}>
      <header className={styles.header}>
        <div>
          <span className={styles.eyebrow}>Inteligência de fontes</span>
          <h1>NEWS TERMINAL</h1>
          <p>
            Fatos, evidências, cobertura e análises do NEWS POOL. O Terminal nunca transforma
            repetição de uma publicação em confirmação independente. O pool ainda não alimenta a
            esteira editorial — os fatos daqui não viram pauta nem matéria sozinhos (ponte em
            construção).
          </p>
        </div>
      </header>

      <nav className={styles.tabs} role="tablist" aria-label="NEWS TERMINAL">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={tab === item.id ? styles.tabActive : undefined}
            onClick={() => setTab(item.id)}
          >
            <strong>{item.label}</strong>
            <span>{item.description}</span>
          </button>
        ))}
      </nav>

      <div role="tabpanel">
        {tab === 'terminal' && <TerminalTab onOpen={setDetail} />}
        {tab === 'connectors' && <ConnectorsTab isStaff={isStaff} />}
        {tab === 'subscriptions' && <SubscriptionsTab isStaff={isStaff} />}
        {tab === 'automations' && <AutomationsTab isStaff={isStaff} />}
      </div>

      {detail?.kind === 'fact' && <WireFactDrawer id={detail.id} onClose={() => setDetail(null)} />}
      {detail?.kind === 'wire' && <WireDrawer id={detail.id} onClose={() => setDetail(null)} />}
    </section>
  );
}

function TerminalTab({ onOpen }: { onOpen: (target: DetailTarget) => void }) {
  const queryClient = useQueryClient();
  const [eventName, setEventName] = useState('');
  const [partitionKey, setPartitionKey] = useState('');
  const [priority, setPriority] = useState<PoolPriority | ''>('');
  const [streamState, setStreamState] = useState<'connected' | 'reconnecting' | 'degraded'>(
    'reconnecting',
  );
  const [streamError, setStreamError] = useState<string | null>(null);
  const [streamGeneration, setStreamGeneration] = useState(0);

  const filters = useMemo(
    () => ({
      eventName: eventName || undefined,
      partitionKey: partitionKey.trim() || undefined,
      priority: priority || undefined,
      limit: 100,
    }),
    [eventName, partitionKey, priority],
  );

  const events = useQuery({
    queryKey: ['feeddrive', 'pool', 'events', filters],
    queryFn: () => feedDriveService.getPoolEvents(filters),
    refetchOnWindowFocus: false,
  });
  const streamCursor = events.data?.next_cursor;
  useEffect(() => {
    if (streamCursor === undefined) return;
    const controller = new AbortController();
    let active = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let lastEventId = queryClient
      .getQueryData<PoolEventsResponse>(['feeddrive', 'pool', 'events', filters])
      ?.items.at(-1)
      ?.sequence.toString();
    const retryDelays = [1_000, 2_000, 5_000, 10_000];

    const connect = async () => {
      let attempt = 0;
      while (active && !controller.signal.aborted) {
        setStreamState('reconnecting');
        try {
          await feedDriveService.streamPoolEvents(
            { ...filters, cursor: streamCursor },
            (incoming) => {
              lastEventId = incoming.sequence.toString();
              setStreamState('connected');
              setStreamError(null);
              queryClient.setQueryData<PoolEventsResponse>(
                ['feeddrive', 'pool', 'events', filters],
                (current) => {
                  const existing = current?.items ?? [];
                  if (existing.some((item) => item.pool_event_id === incoming.pool_event_id)) {
                    return current;
                  }
                  return {
                    items: [...existing, incoming].slice(-100),
                    next_cursor: current?.next_cursor ?? null,
                    has_more: current?.has_more ?? false,
                  };
                },
              );
            },
            controller.signal,
            lastEventId,
            () => {
              setStreamState('connected');
              setStreamError(null);
            },
          );
          if (active && !controller.signal.aborted) {
            throw new Error('NEWS POOL stream desconectado pelo servidor.');
          }
        } catch (error) {
          if (!active || controller.signal.aborted) return;
          setStreamState('degraded');
          setStreamError(apiError(error));
          const delay = retryDelays[Math.min(attempt, retryDelays.length - 1)];
          attempt += 1;
          await new Promise<void>((resolve) => {
            retryTimer = setTimeout(resolve, delay);
          });
        }
      }
    };

    void connect();
    return () => {
      active = false;
      controller.abort();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [filters, queryClient, streamCursor, streamGeneration]);
  const health = useQuery({
    queryKey: ['feeddrive', 'pool', 'health'],
    queryFn: feedDriveService.getPoolHealth,
    refetchInterval: 30_000,
  });

  const healthData = health.data as PoolHealth | undefined;
  const sources = healthData?.sources ?? [];
  const degradedSources = sources.filter(
    (source) => source.enabled && !['ok', 'healthy', 'completed'].includes(source.status),
  );
  const hasGap = Boolean(
    healthData?.gap_detected ||
    healthData?.status === 'gap' ||
    events.data?.items.some((event) => event.event_name === 'pool.gap_detected'),
  );
  const effectiveStatus = health.isError
    ? 'unavailable'
    : hasGap
      ? 'gap'
      : !healthData?.status
        ? 'unknown'
        : degradedSources.length
          ? 'degraded'
          : healthData.status;
  return (
    <div className={styles.terminal}>
      <section
        className={`${styles.healthStrip} ${styles[`health_${effectiveStatus}`] ?? ''}`}
        aria-label="Estado do NEWS POOL"
      >
        <div>
          <Badge tone={stateTone(effectiveStatus)}>
            {health.isError
              ? 'Indisponível'
              : !healthData?.status
                ? 'Verificando saúde'
                : hasGap
                  ? 'Recuperando intervalo'
                  : effectiveStatus === 'degraded'
                    ? 'Cobertura parcial'
                    : effectiveStatus === 'healthy'
                      ? 'Vivo'
                      : 'Estado não confirmado'}
          </Badge>
          <strong>
            {health.isError
              ? 'Não foi possível verificar a saúde do pool'
              : !healthData?.status
                ? 'Aguardando resposta real do health BFF'
                : hasGap
                  ? 'Há um intervalo possivelmente perdido'
                  : degradedSources.length
                    ? `${degradedSources.length} fonte(s) com impacto`
                    : effectiveStatus === 'healthy'
                      ? 'Entrega do pool disponível'
                      : 'O health não confirmou nenhuma fonte ativa'}
          </strong>
        </div>
        <dl>
          <div>
            <dt>
              {healthData?.latest_activity_kind === 'ingestion'
                ? 'Última ingestão'
                : 'Último evento'}
            </dt>
            <dd>{relativeTime(healthData?.last_event_at)}</dd>
          </div>
          <div>
            <dt>Atraso reportado</dt>
            <dd>{seconds(healthData?.lag_seconds)}</dd>
          </div>
          <div>
            <dt>Fontes observadas</dt>
            <dd>{sources.length || 'não informado'}</dd>
          </div>
          <div>
            <dt>Replay</dt>
            <dd>{healthData?.replay_in_progress ? 'em andamento' : 'não informado'}</dd>
          </div>
        </dl>
        {health.isError && (
          <p role="alert">
            {apiError(health.error)} · O stream abaixo mantém apenas o dado que o BFF conseguiu
            entregar.
          </p>
        )}
        {degradedSources.length > 0 && (
          <details>
            <summary>Ver fontes afetadas</summary>
            <ul>
              {degradedSources.map((source) => (
                <li key={source.connector_id}>
                  <strong>{source.connector_id}</strong> · {source.status}
                  {source.error_detail ? ` · ${source.error_detail}` : ''}
                </li>
              ))}
            </ul>
          </details>
        )}
        <div className={styles.streamStatus} role="status" aria-live="polite">
          <Badge
            tone={streamState === 'connected' ? 'ok' : streamState === 'degraded' ? 'crit' : 'warn'}
          >
            stream{' '}
            {streamState === 'connected'
              ? 'conectado'
              : streamState === 'degraded'
                ? 'degradado'
                : 'reconectando'}
          </Badge>
          <span>
            {streamState === 'connected'
              ? 'Atualizações ao vivo sem recarregar a amostra inicial.'
              : streamState === 'degraded'
                ? 'A última amostra real permanece visível; a reconexão usa backoff limitado.'
                : 'Reconectando a partir da última sequência recebida.'}
          </span>
        </div>
        {streamError && (
          <p role="alert" className={styles.streamError}>
            {streamError} Ação: aguarde a reconexão automática ou use Reconectar stream. A consulta
            inicial não será refeita sobre o estado vivo.
          </p>
        )}
      </section>

      <div className={styles.workspace}>
        <aside className={styles.filters}>
          <div className={styles.filterHead}>
            <strong>Recorte do pool</strong>
            <button
              type="button"
              onClick={() => {
                setEventName('');
                setPartitionKey('');
                setPriority('');
              }}
            >
              limpar
            </button>
          </div>
          <label>
            <span>Tipo de mudança</span>
            <select value={eventName} onChange={(event) => setEventName(event.target.value)}>
              <option value="">Todos</option>
              {EVENT_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {EVENT_LABELS[value]}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Chave/partição</span>
            <input
              value={partitionKey}
              onChange={(event) => setPartitionKey(event.target.value)}
              placeholder="ex.: bcb.selic"
            />
          </label>
          <label>
            <span>Prioridade</span>
            <select
              value={priority}
              onChange={(event) => setPriority(event.target.value as PoolPriority | '')}
            >
              <option value="">Todas</option>
              <option value="critical">Crítica</option>
              <option value="urgent">Urgente</option>
              <option value="high">Alta</option>
              <option value="normal">Normal</option>
              <option value="background">Contexto</option>
            </select>
          </label>
          <p className={styles.help}>
            Filtros consultam o BFF. Nenhum item é inventado para preencher o Terminal.
          </p>
        </aside>

        <main className={styles.results}>
          <div className={styles.resultsHead}>
            <div>
              <strong>{events.data?.items.length ?? 0} eventos do pool</strong>
              <span>
                {events.data?.items[0]
                  ? `mais recente há ${relativeTime(events.data.items.at(-1)?.emitted_at)}`
                  : 'aguardando dados reais'}
              </span>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setStreamGeneration((value) => value + 1)}
              loading={streamState === 'reconnecting'}
            >
              Reconectar stream
            </Button>
          </div>

          {events.isLoading && (
            <State
              title="Conectando ao NEWS POOL"
              detail="Buscando o primeiro lote autorizado para este tenant."
            />
          )}
          {events.isError && (
            <State title="NEWS POOL indisponível" detail={apiError(events.error)} error />
          )}
          {events.isSuccess && events.data.items.length === 0 && (
            <State
              title="Nenhum acontecimento neste recorte"
              detail="Remova filtros ou aguarde a próxima entrega. O sistema não usa dados de demonstração nesta tela."
            />
          )}
          <div className={styles.eventList}>
            {(events.data?.items ?? []).map((event) => (
              <PoolEventCard key={event.pool_event_id} event={event} onOpen={onOpen} />
            ))}
          </div>
          {events.data?.has_more && (
            <p className={styles.more}>
              Há mais eventos após este cursor. A paginação será retomada a partir do cursor
              retornado pelo pool.
            </p>
          )}
        </main>
      </div>
    </div>
  );
}

function PoolEventCard({
  event,
  onOpen,
}: {
  event: PoolEvent;
  onOpen: (target: DetailTarget) => void;
}) {
  const isCorrection = event.event_name.includes('revised');
  const isConflict = event.event_name.includes('conflicted');
  const isGap = event.event_name.includes('gap');
  const isDegraded = event.event_name.includes('degraded');
  return (
    <article className={`${styles.eventCard} ${isConflict || isGap ? styles.eventCritical : ''}`}>
      <header>
        <div className={styles.badges}>
          <Badge tone={eventTone(event.event_name)}>{eventTitle(event)}</Badge>
          <Badge tone={priorityTone(event.priority)}>{event.priority}</Badge>
          {isCorrection && <Badge tone="warn">corrigido</Badge>}
          {isConflict && <Badge tone="crit">contradito</Badge>}
          {isGap && <Badge tone="crit">gap</Badge>}
          {isDegraded && <Badge tone="warn">degradado</Badge>}
        </div>
        <time dateTime={event.emitted_at}>{dateTime(event.emitted_at)}</time>
      </header>
      <div className={styles.eventBody}>
        <div>
          <strong>{event.connector_id ?? event.partition_key}</strong>
          <span>
            ocorreu {dateTime(event.occurred_at)} · entregue com{' '}
            {seconds(
              Math.max(
                0,
                Math.round(
                  (new Date(event.emitted_at).getTime() - new Date(event.occurred_at).getTime()) /
                    1000,
                ),
              ),
            )}
          </span>
        </div>
        {event.alert_reason_codes.length > 0 && (
          <ul className={styles.reasonCodes}>
            {event.alert_reason_codes.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        )}
      </div>
      <footer>
        <span>
          sequência {event.sequence} · schema {event.payload_schema_id ?? 'não informado'}
        </span>
        <div>
          {event.wire_fact_id && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onOpen({ kind: 'fact', id: event.wire_fact_id as string })}
            >
              Abrir fato
            </Button>
          )}
          {event.wire_id && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onOpen({ kind: 'wire', id: event.wire_id as string })}
            >
              Abrir fio
            </Button>
          )}
        </div>
      </footer>
    </article>
  );
}

function ConnectorsTab({ isStaff }: { isStaff: boolean }) {
  const connectors = useQuery({
    queryKey: ['feeddrive', 'connectors'],
    queryFn: feedDriveService.getConnectors,
    refetchInterval: 60_000,
    // Console staff-only por desenho (B-36): fora da casa o GET falha e o
    // alarme global dispararia sem nada a fazer com ele. Não perguntar é a
    // resposta honesta — a parede abaixo diz por quê.
    enabled: isStaff,
  });
  // Erro sem nenhum dado em mãos: o painel imprimiria "Nenhum conector
  // registrado", que é exatamente a lista vazia que este conserto veio matar.
  // Com dado em cache o painel fica (é lista real) e o erro do refetch aparece
  // acima dele.
  const painelEmCartaz = isStaff && (connectors.data !== undefined || !connectors.isError);
  return (
    <div className={styles.tabContent}>
      <div className={styles.sectionIntro}>
        <div>
          <h2>Conectores</h2>
          <p>
            Cargas controladas e configuração persistente. Este console mostra JSON pré-ETL real e
            não publica.
          </p>
        </div>
        <ConnectorsBadge
          isStaff={isStaff}
          failed={connectors.isError}
          count={connectors.data?.length}
        />
      </div>
      {!isStaff && (
        <p className={styles.notice} role="status">
          {PAREDE_TENANT_B36}
        </p>
      )}
      {isStaff && connectors.isError && (
        <State
          title="Não foi possível listar os conectores"
          detail={detalheLegivel(
            connectors.error,
            'A casa não conseguiu listar os conectores do NEWS POOL.',
          )}
          error
        />
      )}
      {painelEmCartaz && (
        <ConnectorLoadPanel
          connectors={connectors.data ?? []}
          loading={connectors.isLoading}
          // O erro já está impresso acima com o literal do backend; repassar
          // `failed` trocaria essa frase pela genérica do painel.
          failed={false}
        />
      )}
    </div>
  );
}

/**
 * A contagem só é verdade quando a lista chegou. Fora da casa não há chamada, e
 * numa falha "0 cadastrados" seria a mentira do F-494 escrita em cima do erro.
 */
function ConnectorsBadge({
  isStaff,
  failed,
  count,
}: {
  isStaff: boolean;
  failed: boolean;
  count?: number;
}) {
  if (!isStaff) return <Badge tone="neutral">console da casa</Badge>;
  if (failed && count === undefined) return <Badge tone="crit">não foi possível listar</Badge>;
  if (count === undefined) return <Badge tone="neutral">carregando</Badge>;
  return <Badge tone={failed ? 'crit' : 'neutral'}>{count} cadastrados</Badge>;
}

function SubscriptionsTab({ isStaff }: { isStaff: boolean }) {
  const qc = useQueryClient();
  const role = useAuth((state) => state.role);
  // B-36: o pool-tenant tem dono — staff observa, não opera. O canManage é o
  // gate que TODOS os botões de mutação desta aba já respeitam; zerá-lo para
  // staff fecha pausar/editar/remover de uma vez (F-493).
  const canManage = role === 'admin' && !isStaff;
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const subscriptions = useQuery({
    queryKey: ['feeddrive', 'subscriptions'],
    queryFn: feedDriveService.getSubscriptions,
  });
  const triggers = useQuery({
    queryKey: ['feeddrive', 'triggers'],
    queryFn: feedDriveService.getTriggers,
  });
  const watchlists = useQuery({
    queryKey: ['feeddrive', 'watchlists'],
    queryFn: feedDriveService.getWatchlists,
  });
  const create = useMutation({
    mutationFn: feedDriveService.createSubscription,
    onSuccess: () => {
      setShowForm(false);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'subscriptions'] });
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) =>
      feedDriveService.setSubscriptionPaused(id, paused),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feeddrive', 'subscriptions'] }),
  });
  const update = useMutation({
    mutationFn: ({ id, value }: { id: string; value: TenantSubscriptionInput }) =>
      feedDriveService.updateSubscription(id, value),
    onSuccess: () => {
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'subscriptions'] });
    },
  });
  const remove = useMutation({
    mutationFn: feedDriveService.deleteSubscription,
    onSuccess: () => {
      setRemoveId(null);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'subscriptions'] });
    },
  });

  return (
    <div className={styles.tabContent}>
      {isStaff && (
        <p className={styles.notice} role="status">
          {PAREDE_STAFF_B36}
        </p>
      )}
      <div className={styles.sectionIntro}>
        <div>
          <h2>Minhas assinaturas</h2>
          <p>
            Recortes versionados do pool. A captura e a cadência das fontes continuam no WIRESERVER.
          </p>
        </div>
        <Button
          disabled={!canManage || isStaff}
          title={isStaff ? PAREDE_STAFF_B36 : undefined}
          onClick={() => setShowForm((value) => !value)}
        >
          {showForm ? 'Cancelar' : 'Nova assinatura'}
        </Button>
      </div>
      {/*
        A linha em pool_subscription e real e sobrevive ao reload, mas o unico
        leitor dela e dry_run_pool_params (services/pool_tenant.py:1570-1590),
        chamado so pelo clique manual em "Executar teste sem ligar": nenhum laco
        de fundo (main.py:535-542) avalia evento novo contra assinatura e
        subscription.cursor nunca avanca (F-261).
      */}
      <p className={styles.notice} role="status">
        O recorte fica salvo; a avaliação contínua contra o pool chega com o executor
        (em construção).
      </p>
      {!canManage && (
        <p className={styles.notice}>Somente admin pode criar, pausar ou remover assinaturas.</p>
      )}
      {showForm && (
        <SubscriptionForm
          busy={create.isPending}
          error={create.error}
          onSubmit={(value) => create.mutate(value)}
        />
      )}
      {subscriptions.isLoading && (
        <State
          title="Carregando assinaturas"
          detail="Consultando somente as regras deste tenant."
        />
      )}
      {subscriptions.isError && (
        <State
          title="Não foi possível carregar assinaturas"
          detail={apiError(subscriptions.error)}
          error
        />
      )}
      {subscriptions.isSuccess && subscriptions.data.length === 0 && !showForm && (
        <State
          title="Nenhuma assinatura ainda"
          detail="Crie um recorte para acompanhar entidades, países, idiomas ou tipos de mudança do NEWS POOL."
        />
      )}
      <div className={styles.ruleList}>
        {(subscriptions.data ?? []).map((item) => (
          <article key={item.id} className={styles.ruleCard}>
            <header>
              <div>
                <h3>{item.name}</h3>
                <span>
                  {item.event_types.map((name) => EVENT_LABELS[name] ?? name).join(' · ')}
                </span>
              </div>
              <Badge tone={stateTone(item.status)}>{item.status}</Badge>
            </header>
            <dl>
              <div>
                <dt>Último evento</dt>
                <dd>{dateTime(item.last_event_at)}</dd>
              </div>
              <div>
                <dt>Idade máxima no pool</dt>
                <dd>{seconds(item.max_freshness_seconds)}</dd>
              </div>
              <div>
                <dt>Autoridade exigida</dt>
                <dd>{item.minimum_authority ?? 'não exigida'}</dd>
              </div>
              <div>
                <dt>Schema aceito</dt>
                <dd>major {item.accepted_schema_major}</dd>
              </div>
            </dl>
            <details>
              <summary>Ver contrato do recorte</summary>
              <pre>
                {JSON.stringify(
                  { filters: item.filters, epistemic_classes: item.epistemic_classes },
                  null,
                  2,
                )}
              </pre>
            </details>
            <footer>
              <Button
                size="sm"
                variant="secondary"
                disabled={!canManage}
                onClick={() => setEditingId(editingId === item.id ? null : item.id)}
              >
                {editingId === item.id ? 'Cancelar edição' : 'Editar assinatura'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={!canManage}
                loading={toggle.isPending && toggle.variables?.id === item.id}
                onClick={() => toggle.mutate({ id: item.id, paused: item.status !== 'paused' })}
              >
                {item.status === 'paused' ? 'Reativar assinatura' : 'Pausar assinatura'}
              </Button>
              {removeId === item.id ? (
                <>
                  <span>Remover preserva a auditoria.</span>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={remove.isPending}
                    onClick={() => remove.mutate(item.id)}
                  >
                    Confirmar remoção
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRemoveId(null)}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!canManage}
                  onClick={() => setRemoveId(item.id)}
                >
                  Remover
                </Button>
              )}
            </footer>
            {toggle.isError && toggle.variables?.id === item.id && (
              <ControlError
                error={toggle.error}
                action="Atualize a assinatura, confira o estado atual e tente novamente."
              />
            )}
            {remove.isError && remove.variables === item.id && (
              <ControlError
                error={remove.error}
                action="Desvincule gatilhos e automações, ou pause a assinatura para preservar o histórico, e tente novamente."
              />
            )}
            {editingId === item.id && (
              <SubscriptionForm
                initial={item}
                busy={update.isPending}
                error={update.error}
                onSubmit={(value) => update.mutate({ id: item.id, value })}
              />
            )}
          </article>
        ))}
      </div>
      <TenantTriggers
        items={triggers.data ?? []}
        watchlists={watchlists.data ?? []}
        subscriptions={subscriptions.data ?? []}
        loading={triggers.isLoading}
        error={triggers.error}
        canManage={canManage}
      />
      <TenantWatchlistPanel
        items={watchlists.data ?? []}
        loading={watchlists.isLoading}
        error={watchlists.error}
        canManage={canManage}
      />
    </div>
  );
}

function SubscriptionForm({
  initial,
  busy,
  error,
  onSubmit,
}: {
  initial?: TenantSubscription;
  busy: boolean;
  error: unknown;
  onSubmit: (value: TenantSubscriptionInput) => void;
}) {
  const initialFilter = (key: string) => formatFilterValue(initial?.filters[key]);
  const initialFilterIsArray = (key: string) => Array.isArray(initial?.filters[key]);
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [schemaMajor, setSchemaMajor] = useState(String(initial?.accepted_schema_major ?? 1));
  const [eventTypes, setEventTypes] = useState(
    initial?.event_types.join(', ') ?? 'wire_fact.created',
  );
  const [entity, setEntity] = useState(initialFilter('entity'));
  const [country, setCountry] = useState(initialFilter('country'));
  const [language, setLanguage] = useState(initialFilter('language'));
  const [partitionKey, setPartitionKey] = useState(initialFilter('partition_key'));
  const [connectorId, setConnectorId] = useState(initialFilter('connector_id'));
  const [priority, setPriority] = useState(initialFilter('priority'));
  const [authority, setAuthority] = useState(initial?.minimum_authority ?? '');
  const [epistemicClasses, setEpistemicClasses] = useState(
    initial?.epistemic_classes.join(', ') ?? '',
  );
  const [freshness, setFreshness] = useState(
    initial ? String(initial.max_freshness_seconds ?? '') : '900',
  );
  const submit = () => {
    let filters: Record<string, unknown> = {};
    filters = setFilterValue(filters, 'entity', entity, initialFilterIsArray('entity'));
    filters = setFilterValue(filters, 'country', country, initialFilterIsArray('country'));
    filters = setFilterValue(filters, 'language', language, initialFilterIsArray('language'));
    filters = setFilterValue(
      filters,
      'partition_key',
      partitionKey,
      initialFilterIsArray('partition_key'),
    );
    filters = setFilterValue(
      filters,
      'connector_id',
      connectorId,
      initialFilterIsArray('connector_id'),
    );
    filters = setFilterValue(filters, 'priority', priority, initialFilterIsArray('priority'));
    onSubmit({
      name: name.trim(),
      description: description.trim() || null,
      accepted_schema_major: Number(schemaMajor),
      event_types: parseList(eventTypes),
      filters,
      minimum_authority: authority || null,
      epistemic_classes: parseList(epistemicClasses),
      max_freshness_seconds: freshness ? Number(freshness) : null,
    });
  };
  return (
    <section
      className={styles.formCard}
      aria-label={initial ? 'Editar assinatura' : 'Nova assinatura'}
    >
      <h3>{initial ? 'Editar assinatura' : 'Nova assinatura'}</h3>
      <div className={styles.formGrid}>
        <label className={styles.wide}>
          <span>Nome</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Ex.: Mudanças no Banco Central"
          />
        </label>
        <label className={styles.wide}>
          <span>Descrição</span>
          <input value={description} onChange={(event) => setDescription(event.target.value)} />
        </label>
        <label>
          <span>Schema major aceito</span>
          <input
            type="number"
            min="1"
            value={schemaMajor}
            onChange={(event) => setSchemaMajor(event.target.value)}
          />
        </label>
        <label className={styles.wide}>
          <span>Tipos de mudança, separados por vírgula</span>
          <textarea
            value={eventTypes}
            onChange={(event) => setEventTypes(event.target.value)}
            rows={2}
          />
        </label>
        <label>
          <span>Entidade(s)</span>
          <input
            value={entity}
            onChange={(event) => setEntity(event.target.value)}
            placeholder="ID ou alias"
          />
        </label>
        <label>
          <span>País(es)</span>
          <input
            value={country}
            onChange={(event) => setCountry(event.target.value.toUpperCase())}
            placeholder="BR, US"
          />
        </label>
        <label>
          <span>Idioma(s)</span>
          <input
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            placeholder="pt-BR"
          />
        </label>
        <label>
          <span>Chave(s)/partição(ões)</span>
          <input value={partitionKey} onChange={(event) => setPartitionKey(event.target.value)} />
        </label>
        <label>
          <span>Conector(es)</span>
          <input value={connectorId} onChange={(event) => setConnectorId(event.target.value)} />
        </label>
        <label>
          <span>Prioridade(s)</span>
          <input value={priority} onChange={(event) => setPriority(event.target.value)} />
        </label>
        <label>
          <span>Autoridade exigida</span>
          <select value={authority} onChange={(event) => setAuthority(event.target.value)}>
            <option value="">Nenhuma exigência</option>
            <option value="official">Oficial</option>
            <option value="originator">Originadora</option>
            <option value="regulated">Regulada</option>
            <option value="publisher">Veículo</option>
          </select>
        </label>
        <label className={styles.wide}>
          <span>Classes epistemológicas, separadas por vírgula</span>
          <textarea
            value={epistemicClasses}
            onChange={(event) => setEpistemicClasses(event.target.value)}
            rows={2}
          />
        </label>
        <label>
          <span>Idade máxima do evento no pool (s)</span>
          <input
            type="number"
            min="1"
            value={freshness}
            onChange={(event) => setFreshness(event.target.value)}
          />
        </label>
      </div>
      <p className={styles.help}>
        Após o BFF validar filtros, direitos e schema, a assinatura fica disponível para consulta e
        teste. Nenhuma coleta contínua ou URL de origem é configurada aqui.
      </p>
      {Boolean(error) && (
        <ControlError error={error} action="Revise os campos e tente salvar novamente." />
      )}
      <Button
        loading={busy}
        disabled={!name.trim() || !schemaMajor || parseList(eventTypes).length === 0}
        onClick={submit}
      >
        {initial ? 'Salvar assinatura' : 'Criar assinatura'}
      </Button>
    </section>
  );
}

function TenantWatchlistPanel({
  items,
  loading,
  error,
  canManage,
}: {
  items: TenantWatchlist[];
  loading: boolean;
  error: unknown;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: feedDriveService.createWatchlist,
    onSuccess: () => {
      setShowForm(false);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'watchlists'] });
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      feedDriveService.setWatchlistEnabled(id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feeddrive', 'watchlists'] }),
  });
  const update = useMutation({
    mutationFn: ({ id, value }: { id: string; value: TenantWatchlistInput }) =>
      feedDriveService.updateWatchlist(id, value),
    onSuccess: () => {
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'watchlists'] });
    },
  });
  const remove = useMutation({
    mutationFn: feedDriveService.deleteWatchlist,
    onSuccess: () => {
      setRemoveId(null);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'watchlists'] });
    },
  });
  return (
    <section className={styles.operationSection} aria-labelledby="watchlist-title">
      <div className={styles.sectionIntro}>
        <div>
          <h2 id="watchlist-title">Watchlists de entidades</h2>
          <p>Entidades e aliases comparados ao sujeito de um WireFact real durante o teste.</p>
        </div>
        <Button disabled={!canManage} onClick={() => setShowForm((value) => !value)}>
          {showForm ? 'Cancelar' : 'Nova watchlist'}
        </Button>
      </div>
      {showForm && (
        <WatchlistForm
          busy={create.isPending}
          error={create.error}
          onSubmit={(value) => create.mutate(value)}
        />
      )}
      {loading && (
        <State title="Carregando watchlists" detail="Consultando entidades deste tenant." />
      )}
      {Boolean(error) && (
        <State title="Não foi possível carregar watchlists" detail={apiError(error)} error />
      )}
      {!loading && !error && items.length === 0 && !showForm && (
        <State
          title="Nenhuma watchlist"
          detail="Crie uma lista e associe-a a um gatilho para testar matching de entidade real."
        />
      )}
      <div className={styles.compactGrid}>
        {items.map((item) => (
          <article key={item.id} className={styles.compactCard}>
            <header>
              <div>
                <h3>{item.name}</h3>
                <span>{item.entries.length} entidade(s)</span>
              </div>
              <Badge tone={item.enabled ? 'ok' : 'neutral'}>
                {item.enabled ? 'ligada' : 'desligada'}
              </Badge>
            </header>
            <ul className={styles.reasonCodes}>
              {item.entries.map((entry) => (
                <li key={entry.entity_id}>{entry.label || entry.entity_id}</li>
              ))}
            </ul>
            <footer>
              <Button
                size="sm"
                variant="secondary"
                disabled={!canManage}
                onClick={() => setEditingId(editingId === item.id ? null : item.id)}
              >
                {editingId === item.id ? 'Cancelar edição' : 'Editar watchlist'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={!canManage}
                loading={toggle.isPending && toggle.variables?.id === item.id}
                onClick={() => toggle.mutate({ id: item.id, enabled: !item.enabled })}
              >
                {item.enabled ? 'Desligar watchlist' : 'Ligar watchlist'}
              </Button>
              {removeId === item.id ? (
                <>
                  <span>Gatilhos vinculados precisam ser removidos primeiro.</span>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={remove.isPending}
                    onClick={() => remove.mutate(item.id)}
                  >
                    Confirmar remoção
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRemoveId(null)}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!canManage}
                  onClick={() => setRemoveId(item.id)}
                >
                  Remover
                </Button>
              )}
            </footer>
            {toggle.isError && toggle.variables?.id === item.id && (
              <ControlError
                error={toggle.error}
                action="Atualize a watchlist, confira o estado atual e tente novamente."
              />
            )}
            {remove.isError && remove.variables === item.id && (
              <ControlError
                error={remove.error}
                action="Desvincule a watchlist dos gatilhos e tente novamente."
              />
            )}
            {editingId === item.id && (
              <WatchlistForm
                initial={item}
                busy={update.isPending}
                error={update.error}
                onSubmit={(value) => update.mutate({ id: item.id, value })}
              />
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function WatchlistForm({
  initial,
  busy,
  error,
  onSubmit,
}: {
  initial?: TenantWatchlist;
  busy: boolean;
  error: unknown;
  onSubmit: (value: TenantWatchlistInput) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [entries, setEntries] = useState<TenantWatchlist['entries']>(
    initial?.entries ?? [{ entity_id: '', entity_type: null, label: null, aliases: [] }],
  );
  const [aliasDrafts, setAliasDrafts] = useState(
    (initial?.entries ?? [{ aliases: [] }]).map((entry) => entry.aliases.join(', ')),
  );
  return (
    <section
      className={styles.formCard}
      aria-label={initial ? 'Editar watchlist' : 'Nova watchlist'}
    >
      <h3>{initial ? 'Editar watchlist' : 'Nova watchlist'}</h3>
      <div className={styles.formGrid}>
        <label>
          <span>Nome</span>
          <input value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <label className={styles.wide}>
          <span>Descrição</span>
          <input value={description} onChange={(event) => setDescription(event.target.value)} />
        </label>
        {entries.map((entry, index) => (
          <div className={styles.entryFields} key={index}>
            <label>
              <span>ID da entidade {index + 1}</span>
              <input
                value={entry.entity_id}
                onChange={(event) =>
                  setEntries((current) =>
                    current.map((value, position) =>
                      position === index ? { ...value, entity_id: event.target.value } : value,
                    ),
                  )
                }
              />
            </label>
            <label>
              <span>Rótulo {index + 1}</span>
              <input
                value={entry.label ?? ''}
                onChange={(event) =>
                  setEntries((current) =>
                    current.map((value, position) =>
                      position === index ? { ...value, label: event.target.value || null } : value,
                    ),
                  )
                }
              />
            </label>
            <label>
              <span>Tipo da entidade {index + 1}</span>
              <input
                value={entry.entity_type ?? ''}
                onChange={(event) =>
                  setEntries((current) =>
                    current.map((value, position) =>
                      position === index
                        ? { ...value, entity_type: event.target.value || null }
                        : value,
                    ),
                  )
                }
              />
            </label>
            <label>
              <span>Aliases {index + 1}</span>
              <input
                value={aliasDrafts[index] ?? ''}
                onChange={(event) =>
                  setAliasDrafts((current) =>
                    current.map((value, position) =>
                      position === index ? event.target.value : value,
                    ),
                  )
                }
              />
            </label>
            <Button
              size="sm"
              variant="ghost"
              disabled={entries.length === 1}
              onClick={() => {
                setEntries((current) => current.filter((_, position) => position !== index));
                setAliasDrafts((current) => current.filter((_, position) => position !== index));
              }}
            >
              Remover entidade {index + 1}
            </Button>
          </div>
        ))}
      </div>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => {
          setEntries((current) => [
            ...current,
            { entity_id: '', entity_type: null, label: null, aliases: [] },
          ]);
          setAliasDrafts((current) => [...current, '']);
        }}
      >
        Adicionar entidade
      </Button>
      {Boolean(error) && (
        <ControlError error={error} action="Revise as entidades e tente salvar novamente." />
      )}
      <Button
        loading={busy}
        disabled={!name.trim() || entries.some((entry) => !entry.entity_id.trim())}
        onClick={() =>
          onSubmit({
            name: name.trim(),
            description: description.trim() || null,
            entries: entries.map((entry, index) => ({
              ...entry,
              aliases: parseList(aliasDrafts[index] ?? ''),
            })),
            ...(initial ? {} : { enabled: true }),
          })
        }
      >
        {initial ? 'Salvar watchlist' : 'Criar e ligar'}
      </Button>
    </section>
  );
}

function TenantTriggers({
  items,
  subscriptions,
  watchlists,
  loading,
  error,
  canManage,
}: {
  items: TenantTrigger[];
  subscriptions: Array<{ id: string; name: string }>;
  watchlists: TenantWatchlist[];
  loading: boolean;
  error: unknown;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: feedDriveService.createTrigger,
    onSuccess: () => {
      setShowForm(false);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'triggers'] });
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) =>
      feedDriveService.setTriggerPaused(id, paused),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feeddrive', 'triggers'] }),
  });
  const update = useMutation({
    mutationFn: ({ id, value }: { id: string; value: TenantTriggerInput }) =>
      feedDriveService.updateTrigger(id, value),
    onSuccess: () => {
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'triggers'] });
    },
  });
  const remove = useMutation({
    mutationFn: feedDriveService.deleteTrigger,
    onSuccess: () => {
      setRemoveId(null);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'triggers'] });
    },
  });
  return (
    <section className={styles.operationSection} aria-labelledby="triggers-title">
      <div className={styles.sectionIntro}>
        <div>
          <h2 id="triggers-title">Gatilhos</h2>
          <p>
            Condições auditáveis que explicam por que um evento corresponderia ou ficaria em espera.
          </p>
        </div>
        <Button
          disabled={!canManage || !subscriptions.length}
          onClick={() => setShowForm((value) => !value)}
        >
          {showForm ? 'Cancelar' : 'Novo gatilho'}
        </Button>
      </div>
      {showForm && (
        <TriggerForm
          subscriptions={subscriptions}
          watchlists={watchlists}
          busy={create.isPending}
          error={create.error}
          onSubmit={(value) => create.mutate(value)}
        />
      )}
      {loading && (
        <State title="Carregando gatilhos" detail="Consultando regras versionadas do tenant." />
      )}
      {Boolean(error) && (
        <State title="Não foi possível carregar gatilhos" detail={apiError(error)} error />
      )}
      {!loading && !error && items.length === 0 && !showForm && (
        <State
          title="Nenhum gatilho"
          detail="Crie uma assinatura e depois defina quando ela deve chamar atenção."
        />
      )}
      <div className={styles.compactGrid}>
        {items.map((item) => (
          <article key={item.id} className={styles.compactCard}>
            <header>
              <div>
                <h3>{item.name}</h3>
                <span>assinatura {item.subscription_id}</span>
              </div>
              <Badge tone={item.status === 'active' ? 'ok' : 'neutral'}>{item.status}</Badge>
            </header>
            <details>
              <summary>Ver regra e exceções</summary>
              <pre>
                {JSON.stringify(
                  { rule: item.rule, revision_policy: item.revision_policy },
                  null,
                  2,
                )}
              </pre>
            </details>
            <footer>
              <Button
                size="sm"
                variant="secondary"
                disabled={!canManage}
                onClick={() => setEditingId(editingId === item.id ? null : item.id)}
              >
                {editingId === item.id ? 'Cancelar edição' : 'Editar gatilho'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={!canManage}
                loading={toggle.isPending && toggle.variables?.id === item.id}
                onClick={() => toggle.mutate({ id: item.id, paused: item.status !== 'paused' })}
              >
                {item.status === 'paused' ? 'Ligar gatilho' : 'Pausar gatilho'}
              </Button>
              {removeId === item.id ? (
                <>
                  <span>A auditoria será preservada.</span>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={remove.isPending}
                    onClick={() => remove.mutate(item.id)}
                  >
                    Confirmar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRemoveId(null)}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!canManage}
                  onClick={() => setRemoveId(item.id)}
                >
                  Remover
                </Button>
              )}
            </footer>
            {toggle.isError && toggle.variables?.id === item.id && (
              <ControlError
                error={toggle.error}
                action="Atualize o gatilho, confira o estado atual e tente novamente."
              />
            )}
            {remove.isError && remove.variables === item.id && (
              <ControlError
                error={remove.error}
                action="Desvincule o gatilho das automações e tente novamente."
              />
            )}
            {editingId === item.id && (
              <TriggerForm
                initial={item}
                subscriptions={subscriptions}
                watchlists={watchlists}
                busy={update.isPending}
                error={update.error}
                onSubmit={(value) => update.mutate({ id: item.id, value })}
              />
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function TriggerForm({
  initial,
  subscriptions,
  watchlists,
  busy,
  error,
  onSubmit,
}: {
  initial?: TenantTrigger;
  subscriptions: Array<{ id: string; name: string }>;
  watchlists: TenantWatchlist[];
  busy: boolean;
  error: unknown;
  onSubmit: (value: TenantTriggerInput) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [subscriptionId, setSubscriptionId] = useState(
    initial?.subscription_id ?? subscriptions[0]?.id ?? '',
  );
  const [eventNames, setEventNames] = useState(
    initial?.rule.event_names?.join(', ') ?? 'wire_fact.created',
  );
  const [watchlistId, setWatchlistId] = useState(initial?.watchlist_id ?? '');
  const [partitionKeys, setPartitionKeys] = useState(
    initial?.rule.partition_keys?.join(', ') ?? '',
  );
  const [connectorIds, setConnectorIds] = useState(initial?.rule.connector_ids?.join(', ') ?? '');
  const [priorities, setPriorities] = useState(initial?.rule.priorities?.join(', ') ?? 'normal');
  const [reasonCodes, setReasonCodes] = useState(
    initial?.rule.required_alert_reason_codes?.join(', ') ?? '',
  );
  const [onRevised, setOnRevised] = useState<'accept' | 'hold'>(
    initial?.revision_policy.on_revised ?? 'hold',
  );
  return (
    <section className={styles.formCard} aria-label={initial ? 'Editar gatilho' : 'Novo gatilho'}>
      <h3>{initial ? 'Editar gatilho' : 'Novo gatilho'}</h3>
      <div className={styles.formGrid}>
        <label>
          <span>Nome</span>
          <input value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <label>
          <span>Assinatura</span>
          <select
            value={subscriptionId}
            onChange={(event) => setSubscriptionId(event.target.value)}
          >
            {subscriptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Watchlist de entidades</span>
          <select value={watchlistId} onChange={(event) => setWatchlistId(event.target.value)}>
            <option value="">Sem watchlist</option>
            {watchlists
              .filter((item) => item.enabled || item.id === watchlistId)
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </select>
        </label>
        <label className={styles.wide}>
          <span>Tipos de evento, separados por vírgula</span>
          <textarea
            value={eventNames}
            onChange={(event) => setEventNames(event.target.value)}
            rows={2}
          />
        </label>
        <label className={styles.wide}>
          <span>Chaves/partições, separadas por vírgula</span>
          <textarea
            value={partitionKeys}
            onChange={(event) => setPartitionKeys(event.target.value)}
            rows={2}
          />
        </label>
        <label className={styles.wide}>
          <span>Conectores, separados por vírgula</span>
          <textarea
            value={connectorIds}
            onChange={(event) => setConnectorIds(event.target.value)}
            rows={2}
          />
        </label>
        <label className={styles.wide}>
          <span>Prioridades, separadas por vírgula</span>
          <input value={priorities} onChange={(event) => setPriorities(event.target.value)} />
        </label>
        <label className={styles.wide}>
          <span>Reason codes obrigatórios, separados por vírgula</span>
          <input value={reasonCodes} onChange={(event) => setReasonCodes(event.target.value)} />
        </label>
        <label>
          <span>Quando houver correção</span>
          <select
            value={onRevised}
            onChange={(event) => setOnRevised(event.target.value as 'accept' | 'hold')}
          >
            <option value="hold">Manter em espera</option>
            <option value="accept">Aceitar correção</option>
          </select>
        </label>
      </div>
      <p className={styles.help}>
        Condições diferentes usam AND; valores dentro do mesmo campo usam OR. Retração sempre fica
        em espera.
      </p>
      {Boolean(error) && (
        <ControlError error={error} action="Revise as condições e tente salvar novamente." />
      )}
      <Button
        loading={busy}
        disabled={!name.trim() || !subscriptionId || parseList(eventNames).length === 0}
        onClick={() =>
          onSubmit({
            subscription_id: subscriptionId,
            watchlist_id: watchlistId || null,
            name: name.trim(),
            rule: {
              ...(initial?.rule ?? {}),
              event_names: parseList(eventNames),
              partition_keys: parseList(partitionKeys),
              connector_ids: parseList(connectorIds),
              priorities: parseList(priorities) as PoolPriority[],
              required_alert_reason_codes: parseList(reasonCodes),
            },
            revision_policy: {
              ...(initial?.revision_policy ?? {}),
              on_revised: onRevised,
              on_retracted: 'hold',
            },
          })
        }
      >
        {initial ? 'Salvar gatilho' : 'Criar gatilho'}
      </Button>
    </section>
  );
}

function AutomationsTab({ isStaff }: { isStaff: boolean }) {
  const qc = useQueryClient();
  const role = useAuth((state) => state.role);
  // B-36: o pool-tenant tem dono — staff observa, não opera. O canManage é o
  // gate que TODOS os botões de mutação desta aba já respeitam; zerá-lo para
  // staff fecha pausar/editar/remover de uma vez (F-493).
  const canManage = role === 'admin' && !isStaff;
  const [showForm, setShowForm] = useState(false);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<AutomationRun | null>(null);
  const [killReason, setKillReason] = useState('');
  const automations = useQuery({
    queryKey: ['feeddrive', 'automations'],
    queryFn: feedDriveService.getAutomations,
  });
  const subscriptions = useQuery({
    queryKey: ['feeddrive', 'subscriptions'],
    queryFn: feedDriveService.getSubscriptions,
  });
  const triggers = useQuery({
    queryKey: ['feeddrive', 'triggers'],
    queryFn: feedDriveService.getTriggers,
  });
  const curations = useQuery({
    queryKey: ['feeddrive', 'curations'],
    queryFn: feedDriveService.getCurations,
  });
  const runs = useQuery({
    queryKey: ['feeddrive', 'automation-runs'],
    queryFn: () => feedDriveService.getAutomationRuns({ limit: 50 }),
  });
  // ESCONDIDO: o cartao "Custos das automacoes / Uso supervisionado" saiu da
  // tela porque o numero era preso por construcao. O unico escritor de
  // `pool_automation_run.cost` e create_dry_run (services/pool_tenant.py:1528),
  // que grava literal {"status": "not_instrumented"}; cost_report
  // (pool_tenant.py:1604) so soma linhas com status "instrumented", que nada
  // escreve. Logo "N medido(s)" ficava sempre 0 e total_usd sempre null — um
  // medidor sem instrumento atras. O endpoint, o metodo do service
  // (feedDriveService.getAutomationCostReport) e o componente CostReadout
  // continuam de pe: quando houver instrumentacao, devolver esta query e a
  // <section className={styles.costSummary}> reacende o cartao sem mais nada.
  const tenantKillSwitch = useQuery({
    queryKey: ['feeddrive', 'automation-kill-switch'],
    queryFn: feedDriveService.getAutomationKillSwitch,
    // kill switch é do tenant (_client_id no backend): para staff o GET
    // responderia 400 já no mount da aba (F-493).
    enabled: !isStaff,
  });
  const create = useMutation({
    mutationFn: feedDriveService.createAutomation,
    onSuccess: () => {
      setShowForm(false);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'automations'] });
      qc.invalidateQueries({ queryKey: ['feeddrive', 'automation-kill-switch'] });
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) =>
      feedDriveService.setAutomationPaused(id, paused),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['feeddrive', 'automations'] });
      qc.invalidateQueries({ queryKey: ['feeddrive', 'automation-kill-switch'] });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, value }: { id: string; value: TenantAutomationInput }) =>
      feedDriveService.updateAutomation(id, value),
    onSuccess: () => {
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'automations'] });
      qc.invalidateQueries({ queryKey: ['feeddrive', 'automation-kill-switch'] });
    },
  });
  const remove = useMutation({
    mutationFn: feedDriveService.deleteAutomation,
    onSuccess: () => {
      setRemoveId(null);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'automations'] });
      qc.invalidateQueries({ queryKey: ['feeddrive', 'automation-kill-switch'] });
    },
  });
  const dryRun = useMutation({
    mutationFn: ({ id, cursor }: { id: string; cursor?: string }) =>
      feedDriveService.dryRunAutomation(id, cursor),
    onSuccess: (run) => {
      setSelectedRun(run);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'automation-runs'] });
      qc.invalidateQueries({ queryKey: ['feeddrive', 'automation-costs'] });
    },
  });
  const setTenantKillSwitch = useMutation({
    mutationFn: ({ engaged, reason }: { engaged: boolean; reason: string }) =>
      feedDriveService.setAutomationKillSwitch(engaged, reason),
    onSuccess: () => {
      setKillReason('');
      qc.invalidateQueries({ queryKey: ['feeddrive', 'automation-kill-switch'] });
      qc.invalidateQueries({ queryKey: ['feeddrive', 'automations'] });
    },
  });

  return (
    <div className={styles.tabContent}>
      {isStaff && (
        <p className={styles.notice} role="status">
          {PAREDE_STAFF_B36}
        </p>
      )}
      <div className={styles.sectionIntro}>
        <div>
          <h2>Automações editoriais</h2>
          <p>
            Cada teste avalia um evento real contra assinatura, gatilho, watchlist e curadoria. O
            teste não aciona nenhum destino.
          </p>
        </div>
        <Button
          disabled={!canManage || isStaff || !subscriptions.data?.length}
          title={isStaff ? PAREDE_STAFF_B36 : undefined}
          onClick={() => setShowForm((value) => !value)}
        >
          {showForm ? 'Cancelar' : 'Nova automação'}
        </Button>
      </div>
      {!canManage && (
        <p className={styles.notice}>Somente admin pode criar, pausar ou remover automações.</p>
      )}
      {/*
        ROTULO UNICO da aba (o resto da tela nao repete a mesma frase): as regras
        sao gravadas em pool_automation, mas main.py:535-542 abre 5 lacos de
        fundo e nenhum deles le pool_*. Quem avalia hoje e so o clique em
        "Executar teste sem ligar".
      */}
      <p className={styles.notice} role="status">
        As regras ficam registradas, mas ainda não há executor contínuo que as avalie — hoje só o
        teste supervisionado roda (em construção).
      </p>
      <section className={styles.killSwitch} aria-label="Parada global de regras">
        <div>
          <Badge tone={tenantKillSwitch.data?.engaged ? 'crit' : 'neutral'}>
            {tenantKillSwitch.data?.engaged ? 'parada global ligada' : 'parada global desligada'}
          </Badge>
          <strong>
            {tenantKillSwitch.data?.active_automations ?? 0} de{' '}
            {tenantKillSwitch.data?.total_automations ?? 0} configurações habilitadas
          </strong>
          <span>{tenantKillSwitch.data?.reason || 'Nenhum motivo registrado.'}</span>
          <span>
            Este controle pausa as regras habilitadas e bloqueia a reativação individual enquanto
            estiver ligado.
          </span>
        </div>
        {tenantKillSwitch.isLoading ? (
          <span>Carregando controle…</span>
        ) : tenantKillSwitch.isError ? (
          <span role="alert">{apiError(tenantKillSwitch.error)}</span>
        ) : (
          <>
            <label>
              <span>Motivo obrigatório</span>
              <input value={killReason} onChange={(event) => setKillReason(event.target.value)} />
            </label>
            <Button
              variant={tenantKillSwitch.data?.engaged ? 'secondary' : 'danger'}
              disabled={!canManage || !killReason.trim()}
              loading={setTenantKillSwitch.isPending}
              onClick={() =>
                setTenantKillSwitch.mutate({
                  engaged: !tenantKillSwitch.data?.engaged,
                  reason: killReason.trim(),
                })
              }
            >
              {tenantKillSwitch.data?.engaged
                ? 'Desligar parada global'
                : 'Pausar regras habilitadas'}
            </Button>
          </>
        )}
        {setTenantKillSwitch.isError && (
          <ControlError
            error={setTenantKillSwitch.error}
            action="Atualize o controle, confirme o estado do tenant e tente novamente com um motivo."
          />
        )}
        {tenantKillSwitch.data?.engaged && (
          <p className={styles.notice}>
            Desligar este controle não reativa regras em massa. Reative cada automação
            individualmente.
          </p>
        )}
      </section>
      {/* O cartao de custos foi escondido — ver o comentario na altura das queries. */}
      <TenantCurations
        items={curations.data ?? []}
        subscriptions={subscriptions.data ?? []}
        loading={curations.isLoading}
        error={curations.error}
        canManage={canManage}
        isStaff={isStaff}
      />
      {!subscriptions.isLoading && subscriptions.data?.length === 0 && (
        <div className={styles.notice}>Crie uma assinatura antes de configurar uma automação.</div>
      )}
      {showForm && subscriptions.data && (
        <AutomationForm
          subscriptions={subscriptions.data}
          triggers={triggers.data ?? []}
          curations={curations.data ?? []}
          busy={create.isPending}
          error={create.error}
          onSubmit={(value) => create.mutate(value)}
        />
      )}
      {automations.isLoading && (
        <State
          title="Carregando automações"
          detail="Consultando execuções e kill switches deste tenant."
        />
      )}
      {automations.isError && (
        <State
          title="Não foi possível carregar automações"
          detail={apiError(automations.error)}
          error
        />
      )}
      {automations.isSuccess && automations.data.length === 0 && !showForm && (
        <State
          title="Nenhuma automação"
          detail="Comece em Só observar para medir correspondências sem gerar efeitos."
        />
      )}
      <div className={styles.ruleList}>
        {(automations.data ?? []).map((item) => (
          <article key={item.id} className={styles.ruleCard}>
            <header>
              <div>
                <h3>{item.name}</h3>
                <span>
                  {modeLabel(item.mode)} · responsável {item.owner}
                </span>
                {item.status === 'active' && <span>habilitada para avaliação manual</span>}
              </div>
              <Badge tone={stateTone(item.status)}>{item.status}</Badge>
            </header>
            <dl>
              <div>
                <dt>Kill switch</dt>
                <dd>{item.kill_switch ? 'ligado' : 'desligado'}</dd>
              </div>
              <div>
                <dt>Publicação automática</dt>
                <dd>{item.automatic_publication ? 'permitida' : 'bloqueada'}</dd>
              </div>
            </dl>
            <details>
              <summary>Ver configuração</summary>
              <pre>
                {JSON.stringify(
                  {
                    trigger_id: item.trigger_id,
                    curation_id: item.curation_id,
                    filters: item.filters,
                    model_policy: item.model_policy,
                  },
                  null,
                  2,
                )}
              </pre>
            </details>
            <p className={styles.notice}>
              Destino e orçamento operacional pertencem ao M4b e não estão disponíveis neste corte.
            </p>
            <footer>
              <Button
                size="sm"
                variant="secondary"
                disabled={!canManage}
                onClick={() => setEditingId(editingId === item.id ? null : item.id)}
              >
                {editingId === item.id ? 'Cancelar edição' : 'Editar regra'}
              </Button>
              <Button
                size="sm"
                disabled={!canManage}
                loading={dryRun.isPending && dryRun.variables?.id === item.id}
                onClick={() => dryRun.mutate({ id: item.id })}
              >
                Executar teste sem ligar
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={!canManage}
                loading={toggle.isPending && toggle.variables?.id === item.id}
                onClick={() => toggle.mutate({ id: item.id, paused: item.status !== 'paused' })}
              >
                {item.status === 'paused' ? 'Habilitar regra' : 'Pausar regra'}
              </Button>
              {removeId === item.id ? (
                <>
                  <span>
                    Pause para preservar o histórico. Só é possível remover sem histórico ou
                    dependências.
                  </span>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={remove.isPending}
                    onClick={() => remove.mutate(item.id)}
                  >
                    Confirmar remoção
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRemoveId(null)}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!canManage}
                  onClick={() => setRemoveId(item.id)}
                >
                  Remover
                </Button>
              )}
            </footer>
            {toggle.isError && toggle.variables?.id === item.id && (
              <ControlError
                error={toggle.error}
                action="Atualize a automação, confira o estado atual e tente novamente."
              />
            )}
            {remove.isError && remove.variables === item.id && (
              <ControlError
                error={remove.error}
                action="Pause a automação para preservar o histórico; só é possível remover uma automação sem execuções nem dependências vinculadas."
              />
            )}
            {editingId === item.id && (
              <AutomationForm
                initial={item}
                subscriptions={subscriptions.data ?? []}
                triggers={triggers.data ?? []}
                curations={curations.data ?? []}
                busy={update.isPending}
                error={update.error}
                onSubmit={(value) => update.mutate({ id: item.id, value })}
              />
            )}
          </article>
        ))}
      </div>
      {dryRun.isError && (
        <State title="O teste não pôde ser iniciado" detail={apiError(dryRun.error)} error />
      )}
      {selectedRun && (
        <AutomationRunResult
          run={selectedRun}
          heading="Resultado do teste sem publicação"
          canTestNext={canManage}
          testingNext={dryRun.isPending && dryRun.variables?.cursor != null}
          nextError={dryRun.variables?.cursor != null ? dryRun.error : null}
          onTestNext={(cursor) => dryRun.mutate({ id: selectedRun.automation_id, cursor })}
        />
      )}
      <section className={styles.operationSection} aria-labelledby="runs-title">
        <div className={styles.sectionIntro}>
          <div>
            <h2 id="runs-title">Execuções e testes</h2>
            <p>Histórico real. Nesta fatia, todas as execuções são testes sem publicação.</p>
          </div>
          <Badge tone="neutral">side effects desligados</Badge>
        </div>
        {runs.isLoading && (
          <State
            title="Carregando execuções"
            detail="Consultando o histórico auditável deste tenant."
          />
        )}
        {runs.isError && (
          <State title="Não foi possível carregar execuções" detail={apiError(runs.error)} error />
        )}
        {runs.isSuccess && runs.data.length === 0 && (
          <State
            title="Nenhum teste executado"
            detail="Use Executar teste sem ligar. O BFF selecionará somente um evento real do pool e manterá side effects desligados."
          />
        )}
        <div className={styles.runList}>
          {(runs.data ?? []).map((run) => (
            <AutomationRunResult key={run.id} run={run} />
          ))}
        </div>
      </section>
    </div>
  );
}

function TenantCurations({
  items,
  subscriptions,
  loading,
  error,
  canManage,
  isStaff,
}: {
  items: TenantCuration[];
  subscriptions: Array<{ id: string; name: string }>;
  loading: boolean;
  error: unknown;
  canManage: boolean;
  isStaff: boolean;
}) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: feedDriveService.createCuration,
    onSuccess: () => {
      setShowForm(false);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'curations'] });
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      feedDriveService.setCurationEnabled(id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feeddrive', 'curations'] }),
  });
  const update = useMutation({
    mutationFn: ({ id, value }: { id: string; value: TenantCurationInput }) =>
      feedDriveService.updateCuration(id, value),
    onSuccess: () => {
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'curations'] });
    },
  });
  const remove = useMutation({
    mutationFn: feedDriveService.deleteCuration,
    onSuccess: () => {
      setRemoveId(null);
      qc.invalidateQueries({ queryKey: ['feeddrive', 'curations'] });
    },
  });
  return (
    <section className={styles.operationSection} aria-labelledby="curations-title">
      <div className={styles.sectionIntro}>
        <div>
          <h2 id="curations-title">Curadorias</h2>
          <p>
            Filtros de termos para a simulação. Somente include/exclude altera a decisão de forma
            determinística; não há IA.
          </p>
        </div>
        <Button
          disabled={!canManage || isStaff || !subscriptions.length}
          title={isStaff ? PAREDE_STAFF_B36 : undefined}
          onClick={() => setShowForm((value) => !value)}
        >
          {showForm ? 'Cancelar' : 'Nova curadoria'}
        </Button>
      </div>
      {showForm && (
        <CurationForm
          subscriptions={subscriptions}
          busy={create.isPending}
          error={create.error}
          onSubmit={(value) => create.mutate(value)}
        />
      )}
      {loading && (
        <State title="Carregando curadorias" detail="Consultando filtros privados deste tenant." />
      )}
      {Boolean(error) && (
        <State title="Não foi possível carregar curadorias" detail={apiError(error)} error />
      )}
      {!loading && !error && items.length === 0 && !showForm && (
        <State
          title="Nenhuma curadoria"
          detail="Crie um filtro de termos antes de associá-lo a uma regra supervisionada."
        />
      )}
      <div className={styles.compactGrid}>
        {items.map((item) => (
          <article key={item.id} className={styles.compactCard}>
            <header>
              <div>
                <h3>{item.name}</h3>
                <span>assinatura {item.subscription_id}</span>
              </div>
              <Badge tone={item.enabled ? 'ok' : 'neutral'}>
                {item.enabled ? 'ligada' : 'desligada'}
              </Badge>
            </header>
            <p>
              <strong>Nota interna:</strong> {item.instructions || 'não informada.'}
            </p>
            <details>
              <summary>Ver filtros de curadoria</summary>
              <pre>{JSON.stringify(item.filters, null, 2)}</pre>
            </details>
            <footer>
              <Button
                size="sm"
                variant="secondary"
                disabled={!canManage}
                onClick={() => setEditingId(editingId === item.id ? null : item.id)}
              >
                {editingId === item.id ? 'Cancelar edição' : 'Editar curadoria'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={!canManage}
                loading={toggle.isPending && toggle.variables?.id === item.id}
                onClick={() => toggle.mutate({ id: item.id, enabled: !item.enabled })}
              >
                {item.enabled ? 'Desligar curadoria' : 'Ligar curadoria'}
              </Button>
              {removeId === item.id ? (
                <>
                  <span>A auditoria será preservada.</span>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={remove.isPending}
                    onClick={() => remove.mutate(item.id)}
                  >
                    Confirmar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRemoveId(null)}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!canManage}
                  onClick={() => setRemoveId(item.id)}
                >
                  Remover
                </Button>
              )}
            </footer>
            {toggle.isError && toggle.variables?.id === item.id && (
              <ControlError
                error={toggle.error}
                action="Atualize a curadoria, confira o estado atual e tente novamente."
              />
            )}
            {remove.isError && remove.variables === item.id && (
              <ControlError
                error={remove.error}
                action="Desvincule a curadoria das automações e tente novamente."
              />
            )}
            {editingId === item.id && (
              <CurationForm
                initial={item}
                subscriptions={subscriptions}
                busy={update.isPending}
                error={update.error}
                onSubmit={(value) => update.mutate({ id: item.id, value })}
              />
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function CurationForm({
  initial,
  subscriptions,
  busy,
  error,
  onSubmit,
}: {
  initial?: TenantCuration;
  subscriptions: Array<{ id: string; name: string }>;
  busy: boolean;
  error: unknown;
  onSubmit: (value: TenantCurationInput) => void;
}) {
  const terms = (key: string) => {
    const value = initial?.filters[key];
    return Array.isArray(value)
      ? value.filter((item): item is string => typeof item === 'string')
      : [];
  };
  const [name, setName] = useState(initial?.name ?? '');
  const [subscriptionId, setSubscriptionId] = useState(
    initial?.subscription_id ?? subscriptions[0]?.id ?? '',
  );
  const [instructions, setInstructions] = useState(initial?.instructions ?? '');
  const [include, setInclude] = useState(terms('include').join(', '));
  const [exclude, setExclude] = useState(terms('exclude').join(', '));
  return (
    <section
      className={styles.formCard}
      aria-label={initial ? 'Editar curadoria' : 'Nova curadoria'}
    >
      <h3>{initial ? 'Editar curadoria' : 'Nova curadoria'}</h3>
      <div className={styles.formGrid}>
        <label>
          <span>Nome</span>
          <input value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <label>
          <span>Assinatura</span>
          <select
            value={subscriptionId}
            onChange={(event) => setSubscriptionId(event.target.value)}
          >
            {subscriptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.wide}>
          <span>Nota interna (não altera a decisão neste corte)</span>
          <textarea
            value={instructions}
            onChange={(event) => setInstructions(event.target.value)}
            rows={4}
          />
        </label>
        <label>
          <span>Incluir termos, separados por vírgula</span>
          <input value={include} onChange={(event) => setInclude(event.target.value)} />
        </label>
        <label>
          <span>Excluir termos, separados por vírgula</span>
          <input value={exclude} onChange={(event) => setExclude(event.target.value)} />
        </label>
      </div>
      <p className={styles.help}>
        A nota é apenas persistida. Neste teste sem publicação, somente include/exclude é avaliado
        deterministicamente; nenhuma IA é executada.
      </p>
      {Boolean(error) && (
        <ControlError error={error} action="Revise os termos e tente salvar novamente." />
      )}
      <Button
        loading={busy}
        disabled={!name.trim() || !subscriptionId}
        onClick={() =>
          onSubmit({
            subscription_id: subscriptionId,
            name: name.trim(),
            instructions: instructions.trim(),
            filters: {
              ...(initial?.filters ?? {}),
              include: include
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean),
              exclude: exclude
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean),
            },
          })
        }
      >
        {initial ? 'Salvar curadoria' : 'Criar curadoria'}
      </Button>
    </section>
  );
}

function AutomationRunResult({
  run,
  heading,
  canTestNext = false,
  testingNext = false,
  nextError,
  onTestNext,
}: {
  run: AutomationRun;
  heading?: string;
  canTestNext?: boolean;
  testingNext?: boolean;
  nextError?: unknown;
  onTestNext?: (cursor: string) => void;
}) {
  const state: TenantRunState =
    run.status === 'held' ? 'held' : run.decision.matched ? 'matched' : 'suppressed';
  const measured = run.cost.status === 'instrumented';
  const measuredAmountUsd = run.cost.status === 'instrumented' ? Number(run.cost.amount_usd) : null;
  const errorMessage = typeof run.error.message === 'string' ? run.error.message : null;
  const errorAction = typeof run.error.action === 'string' ? run.error.action : null;
  return (
    <RunStateCard
      state={state}
      title={heading ?? `Teste ${run.id}`}
      idempotencyKey={run.idempotency_key}
      reasonCodes={run.decision.reason_codes}
      errorMessage={errorMessage}
    >
      <div className={styles.runDecision}>
        <div>
          <span>Decisão</span>
          <strong>
            {run.decision.outcome === 'would_propose'
              ? 'Corresponderia aos critérios supervisionados'
              : run.decision.outcome === 'would_observe'
                ? 'Apenas observaria'
                : 'Manteria em espera'}
          </strong>
        </div>
        <div>
          <span>Efeito externo</span>
          <strong>
            {run.decision.side_effects ? 'executado' : 'nenhum · teste sem publicação'}
          </strong>
        </div>
        <CostReadout
          status={measured ? 'measured' : 'not_instrumented'}
          amountUsd={measuredAmountUsd}
        />
      </div>
      {errorAction && (
        <p className={styles.notice}>
          <strong>O que fazer:</strong> {errorAction}
        </p>
      )}
      {run.status === 'held' && run.decision.has_more && run.decision.next_cursor && onTestNext && (
        <div className={styles.nextBatchAction}>
          <Button
            size="sm"
            variant="secondary"
            disabled={!canTestNext}
            loading={testingNext}
            onClick={() => onTestNext(run.decision.next_cursor as string)}
          >
            Testar próximo lote
          </Button>
          <span>Continua a busca real; não liga a regra nem aciona o destino.</span>
          {Boolean(nextError) && (
            <ControlError
              error={nextError}
              action="Mantenha a regra pausada, atualize o resultado e tente este cursor novamente."
            />
          )}
        </div>
      )}
      {run.decision.event && (
        <details>
          <summary>Ver evento real avaliado</summary>
          <pre>{JSON.stringify(run.decision.event, null, 2)}</pre>
        </details>
      )}
    </RunStateCard>
  );
}

function modeLabel(mode: string): string {
  return { shadow: 'Só observar', supervised: 'Avaliar critérios supervisionados' }[mode] ?? mode;
}

function AutomationForm({
  initial,
  subscriptions,
  triggers,
  curations,
  busy,
  error,
  onSubmit,
}: {
  initial?: TenantAutomation;
  subscriptions: Array<{ id: string; name: string }>;
  triggers: TenantTrigger[];
  curations: TenantCuration[];
  busy: boolean;
  error: unknown;
  onSubmit: (value: TenantAutomationInput) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [subscriptionId, setSubscriptionId] = useState(
    initial?.subscription_id ?? subscriptions[0]?.id ?? '',
  );
  const [mode, setMode] = useState<TenantAutomationInput['mode']>(initial?.mode ?? 'shadow');
  const [triggerId, setTriggerId] = useState(initial?.trigger_id ?? '');
  const [curationId, setCurationId] = useState(initial?.curation_id ?? '');
  const [owner, setOwner] = useState(initial?.owner ?? '');
  const [eventTypes, setEventTypes] = useState(initial?.event_types.join(', ') ?? '');
  const initialAutomationFilter = (key: string) => formatFilterValue(initial?.filters[key]);
  const initialAutomationFilterIsArray = (key: string) => Array.isArray(initial?.filters[key]);
  const [partitionKeys, setPartitionKeys] = useState(initialAutomationFilter('partition_key'));
  const [connectorIds, setConnectorIds] = useState(initialAutomationFilter('connector_id'));
  const [priorities, setPriorities] = useState(initialAutomationFilter('priority'));
  const submit = () => {
    let filters: Record<string, unknown> = {};
    filters = setFilterValue(
      filters,
      'partition_key',
      partitionKeys,
      initialAutomationFilterIsArray('partition_key'),
    );
    filters = setFilterValue(
      filters,
      'connector_id',
      connectorIds,
      initialAutomationFilterIsArray('connector_id'),
    );
    filters = setFilterValue(
      filters,
      'priority',
      priorities,
      initialAutomationFilterIsArray('priority'),
    );
    const editable: TenantAutomationInput = {
      name: name.trim(),
      subscription_id: subscriptionId,
      trigger_id: triggerId || null,
      curation_id: curationId || null,
      mode,
      event_types: parseList(eventTypes),
      filters,
      owner: owner.trim(),
    };
    onSubmit(
      initial
        ? editable
        : {
            ...editable,
            model_policy: {},
            destination: {},
            budget: {},
          },
    );
  };
  return (
    <section className={styles.formCard} aria-label={initial ? 'Editar regra' : 'Nova automação'}>
      <h3>{initial ? 'Editar regra' : 'Nova automação'}</h3>
      <div className={styles.formGrid}>
        <label className={styles.wide}>
          <span>Nome</span>
          <input value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <label>
          <span>Assinatura</span>
          <select
            value={subscriptionId}
            onChange={(event) => {
              setSubscriptionId(event.target.value);
              setTriggerId('');
              setCurationId('');
            }}
          >
            {subscriptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Gatilho</span>
          <select value={triggerId} onChange={(event) => setTriggerId(event.target.value)}>
            <option value="">Sem gatilho adicional</option>
            {triggers
              .filter((item) => item.subscription_id === subscriptionId)
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </select>
        </label>
        <label>
          <span>Curadoria</span>
          <select value={curationId} onChange={(event) => setCurationId(event.target.value)}>
            <option value="">Sem curadoria</option>
            {curations
              .filter((item) => item.subscription_id === subscriptionId)
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </select>
        </label>
        <label>
          <span>Modo</span>
          <select
            value={mode}
            onChange={(event) => setMode(event.target.value as TenantAutomationInput['mode'])}
          >
            <option value="shadow">Só observar</option>
            <option value="supervised">Avaliar critérios supervisionados</option>
          </select>
        </label>
        <label>
          <span>Responsável</span>
          <input value={owner} onChange={(event) => setOwner(event.target.value)} />
        </label>
        <label className={styles.wide}>
          <span>Tipos de evento, separados por vírgula</span>
          <textarea
            value={eventTypes}
            onChange={(event) => setEventTypes(event.target.value)}
            rows={2}
          />
        </label>
        <label className={styles.wide}>
          <span>Filtro — partições, separadas por vírgula</span>
          <input value={partitionKeys} onChange={(event) => setPartitionKeys(event.target.value)} />
        </label>
        <label className={styles.wide}>
          <span>Filtro — conectores, separados por vírgula</span>
          <input value={connectorIds} onChange={(event) => setConnectorIds(event.target.value)} />
        </label>
        <label className={styles.wide}>
          <span>Filtro — prioridades, separadas por vírgula</span>
          <input value={priorities} onChange={(event) => setPriorities(event.target.value)} />
        </label>
      </div>
      <p className={styles.help}>
        O BFF consulta uma amostra paginada do pool real; o operador pode avançar lote a lote.
        Nenhum ACK/replay contínuo é feito. No modo Só observar, nenhum efeito externo ocorre.
      </p>
      <p className={styles.notice}>Publicação automática não faz parte deste contrato.</p>
      {Boolean(error) && (
        <ControlError error={error} action="Revise a configuração e tente salvar novamente." />
      )}
      <Button
        loading={busy}
        disabled={!name.trim() || !subscriptionId || !owner.trim()}
        onClick={submit}
      >
        {initial ? 'Salvar regra' : 'Criar automação'}
      </Button>
    </section>
  );
}

function WireFactDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const fact = useQuery({
    queryKey: ['feeddrive', 'wire-fact', id],
    queryFn: () => feedDriveService.getWireFact(id),
  });
  const versions = useQuery({
    queryKey: ['feeddrive', 'wire-fact', id, 'versions'],
    queryFn: () => feedDriveService.getWireFactVersions(id),
  });
  const evidence = useQuery({
    queryKey: ['feeddrive', 'wire-fact', id, 'evidence'],
    queryFn: () => feedDriveService.getWireFactEvidence(id),
  });
  const coverage = useQuery({
    queryKey: ['feeddrive', 'wire-fact', id, 'coverage'],
    queryFn: () => feedDriveService.getWireFactCoverage(id),
  });
  const enrichments = useQuery({
    queryKey: ['feeddrive', 'wire-fact', id, 'enrichments'],
    queryFn: () => feedDriveService.getWireFactEnrichments(id),
  });
  const data = fact.data;
  return (
    <Drawer
      title={data?.fact.subject.label ?? 'Fato'}
      eyebrow={`WireFact · ${id}`}
      onClose={onClose}
    >
      {fact.isLoading && (
        <State title="Carregando fato" detail="Consultando a versão atual autorizada." />
      )}
      {fact.isError && <State title="Fato indisponível" detail={apiError(fact.error)} error />}
      {data && (
        <>
          <div className={styles.badges}>
            <Badge tone={stateTone(data.lifecycle_status)}>
              {LIFECYCLE_LABELS[data.lifecycle_status] ?? data.lifecycle_status}
            </Badge>
            <Badge tone="info">
              {EPISTEMIC_LABELS[data.epistemic_class] ?? data.epistemic_class}
            </Badge>
            <Badge
              tone={
                data.evidence_status === 'conflicted'
                  ? 'crit'
                  : data.evidence_status === 'independently_supported'
                    ? 'ok'
                    : 'warn'
              }
            >
              {data.evidence_status}
            </Badge>
            <Badge tone="neutral">direito {data.rights.class}</Badge>
          </div>
          <section className={styles.factHero}>
            <span>{data.fact.fact_type}</span>
            <h3>
              {data.fact.subject.label} · {data.fact.predicate}
            </h3>
            <strong>{factValue(data)}</strong>
            {data.fact.object.previous_value != null && (
              <p>
                Valor anterior: {String(data.fact.object.previous_value)} · base:{' '}
                {data.fact.object.change_basis ?? 'não informada'}
              </p>
            )}
          </section>
          <DetailSection title="Relógios e atrasos">
            <dl className={styles.clockGrid}>
              <div>
                <dt>Aconteceu</dt>
                <dd>{dateTime(data.time.event_at)}</dd>
              </div>
              <div>
                <dt>Fonte publicou</dt>
                <dd>{dateTime(data.time.source_published_at)}</dd>
              </div>
              <div>
                <dt>Primeira observação</dt>
                <dd>{dateTime(data.time.first_observed_at)}</dd>
              </div>
              <div>
                <dt>Normalizado</dt>
                <dd>{dateTime(data.time.normalized_at)}</dd>
              </div>
              <div>
                <dt>Delay da fonte</dt>
                <dd>{seconds(data.time.delay.source_seconds)}</dd>
              </div>
              <div>
                <dt>Coleta</dt>
                <dd>{seconds(data.time.delay.collection_seconds)}</dd>
              </div>
              <div>
                <dt>ETL</dt>
                <dd>{seconds(data.time.delay.etl_seconds)}</dd>
              </div>
              <div>
                <dt>Pool</dt>
                <dd>{seconds(data.time.delay.pool_seconds)}</dd>
              </div>
            </dl>
          </DetailSection>
          <DetailSection
            title={`Versões (${versions.data?.length ?? 0})`}
            loading={versions.isLoading}
            error={versions.error}
          >
            <div className={styles.timeline}>
              {(versions.data ?? []).map((item) => (
                <article key={item.version}>
                  <strong>Versão {item.version}</strong>
                  <span>normalizada {dateTime(item.normalized_at)}</span>
                  <small>
                    validade: {dateTime(item.valid_from)} → {dateTime(item.valid_to)}
                  </small>
                </article>
              ))}
            </div>
          </DetailSection>
          <DetailSection
            title={`Evidências (${evidence.data?.length ?? 0})`}
            loading={evidence.isLoading}
            error={evidence.error}
          >
            <div className={styles.detailList}>
              {(evidence.data ?? []).map((item) => (
                <article key={item.evidence_id}>
                  <header>
                    <Badge tone={item.role === 'contradiction' ? 'crit' : 'info'}>
                      {item.role}
                    </Badge>
                    <span>{item.authority_class}</span>
                  </header>
                  <strong>
                    {item.connector_id} · {item.source_id}
                  </strong>
                  <p>{item.supports_scope}</p>
                  <footer>
                    <span>
                      {dateTime(item.observed_at)} · {item.byte_size} bytes · RAW{' '}
                      {item.raw_available ? 'permitido' : 'indisponível'}
                    </span>
                    {item.url && (
                      <a href={item.url} target="_blank" rel="noreferrer">
                        Abrir origem
                      </a>
                    )}
                  </footer>
                </article>
              ))}
            </div>
          </DetailSection>
          <DetailSection
            title={`Cobertura (${coverage.data?.length ?? 0})`}
            loading={coverage.isLoading}
            error={coverage.error}
          >
            <div className={styles.detailList}>
              {(coverage.data ?? []).map((item) => (
                <article key={item.coverage_id}>
                  <header>
                    <Badge tone={item.relation === 'contradicts' ? 'crit' : 'neutral'}>
                      {item.relation}
                    </Badge>
                    <span>{item.publisher}</span>
                  </header>
                  <strong>{item.title}</strong>
                  <p>
                    {[
                      item.country_code,
                      item.language,
                      item.rights_class,
                      dateTime(item.published_at),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  <footer>
                    <span>
                      método {item.method} · confiança {pct(item.confidence)}
                    </span>
                    <a href={item.url} target="_blank" rel="noreferrer">
                      Abrir cobertura
                    </a>
                  </footer>
                </article>
              ))}
            </div>
          </DetailSection>
          <DetailSection
            title={`Análises (${enrichments.data?.length ?? 0})`}
            loading={enrichments.isLoading}
            error={enrichments.error}
          >
            <div className={styles.detailList}>
              {(enrichments.data ?? []).map((item) => (
                <article key={item.enrichment_id}>
                  <header>
                    <Badge tone={stateTone(item.status)}>{item.status}</Badge>
                    <span>
                      {item.task} · {item.method}
                    </span>
                  </header>
                  <pre>{JSON.stringify(item.output, null, 2)}</pre>
                  <footer>
                    <span>
                      {item.model ?? item.pipeline_version} · confiança {pct(item.confidence)}
                    </span>
                    <span>
                      {item.cost_usd == null
                        ? 'custo não medido'
                        : `US$ ${item.cost_usd.toFixed(6)}`}
                    </span>
                  </footer>
                </article>
              ))}
            </div>
          </DetailSection>
        </>
      )}
    </Drawer>
  );
}

function WireDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const wire = useQuery({
    queryKey: ['feeddrive', 'wire', id],
    queryFn: () => feedDriveService.getWire(id),
  });
  const timeline = useQuery({
    queryKey: ['feeddrive', 'wire', id, 'timeline'],
    queryFn: () => feedDriveService.getWireTimeline(id),
  });
  const data = wire.data;
  return (
    <Drawer
      title={data?.title ?? 'Fio do acontecimento'}
      eyebrow={`Wire · ${id}`}
      onClose={onClose}
    >
      {wire.isLoading && <State title="Carregando fio" detail="Consultando membros e qualidade." />}
      {wire.isError && <State title="Fio indisponível" detail={apiError(wire.error)} error />}
      {data && (
        <>
          <div className={styles.badges}>
            <Badge tone={stateTone(data.status)}>
              {LIFECYCLE_LABELS[data.status] ?? data.status}
            </Badge>
            <Badge tone={data.quality.conflict_count ? 'crit' : 'neutral'}>
              {data.quality.conflict_count ?? 0} conflito(s)
            </Badge>
            <Badge tone="neutral">versão {data.version}</Badge>
          </div>
          <dl className={styles.clockGrid}>
            <div>
              <dt>Primeira atividade</dt>
              <dd>{dateTime(data.first_activity_at)}</dd>
            </div>
            <div>
              <dt>Última atividade</dt>
              <dd>{dateTime(data.last_activity_at)}</dd>
            </div>
            <div>
              <dt>Novidade</dt>
              <dd>{pct(data.quality.novelty)}</dd>
            </div>
            <div>
              <dt>Materialidade</dt>
              <dd>{pct(data.quality.materiality)}</dd>
            </div>
          </dl>
          <div className={styles.tags}>
            {[...data.categories, ...data.entity_ids, ...data.countries, ...data.languages].map(
              (tag) => (
                <span key={tag}>{tag}</span>
              ),
            )}
          </div>
          {!data.member_page.complete && (
            <p className={styles.notice}>
              Esta projeção contém {data.member_page.returned} de {data.member_page.total} membros.
              O pool não declarou a coleção completa.
            </p>
          )}
          <DetailSection title={`Membros (${data.member_page.total})`}>
            <div className={styles.detailList}>
              {data.members.map((item) => (
                <article key={item.membership_id}>
                  <header>
                    <Badge tone={item.role === 'contradiction' ? 'crit' : 'info'}>
                      {item.role}
                    </Badge>
                    <span>{item.member_type}</span>
                  </header>
                  <strong>{item.member_id}</strong>
                  <p>
                    {item.method} · confiança {pct(item.confidence)}
                  </p>
                  <div className={styles.reasonCodes}>
                    {item.reason_codes.map((reason) => (
                      <span key={reason}>{reason}</span>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </DetailSection>
          <DetailSection
            title={`Linha do tempo (${timeline.data?.length ?? 0})`}
            loading={timeline.isLoading}
            error={timeline.error}
          >
            <div className={styles.timeline}>
              {(timeline.data ?? []).map((item) => (
                <article key={item.pool_event_id}>
                  <Badge tone={eventTone(item.event_name)}>{eventTitle(item)}</Badge>
                  <span>{dateTime(item.occurred_at)}</span>
                  <small>sequência {item.sequence}</small>
                </article>
              ))}
            </div>
          </DetailSection>
        </>
      )}
    </Drawer>
  );
}

function Drawer({
  title,
  eyebrow,
  onClose,
  children,
}: {
  title: string;
  eyebrow: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className={styles.backdrop} onClick={onClose}>
      <aside
        className={styles.drawer}
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className={styles.eyebrow}>{eyebrow}</span>
            <h2>{title}</h2>
          </div>
          <button type="button" onClick={onClose}>
            fechar
          </button>
        </header>
        <div className={styles.drawerBody}>{children}</div>
      </aside>
    </div>
  );
}

function DetailSection({
  title,
  loading,
  error,
  children,
}: {
  title: string;
  loading?: boolean;
  error?: unknown;
  children: ReactNode;
}) {
  return (
    <section className={styles.detailSection}>
      <h3>{title}</h3>
      {loading && <p>Carregando dados reais…</p>}
      {Boolean(error) && (
        <p role="alert" className={styles.error}>
          {apiError(error)}
        </p>
      )}
      {!loading && !error && children}
    </section>
  );
}

function State({
  title,
  detail,
  error = false,
}: {
  title: string;
  detail: string;
  error?: boolean;
}) {
  return (
    <div
      className={`${styles.state} ${error ? styles.stateError : ''}`}
      role={error ? 'alert' : 'status'}
    >
      <strong>{title}</strong>
      <span>{detail}</span>
    </div>
  );
}
